import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { message, notification } from "antd";
import { Context } from "./context";

const TRANSCODE_SECRET =
  process.env.REACT_APP_TRANSCODE_SECRET || "umeshchand";

export const ACTIVE_TRANSCODE_JOB_KEY = "activeTranscodingJob";

function buildTranscodingWebSocketUrl(transcodeUrl, jobId, tokenSecret) {
  try {
    const u = new URL(
      transcodeUrl.startsWith("http") ? transcodeUrl : `https://${transcodeUrl}/`
    );
    const wsProto = u.protocol === "https:" ? "wss:" : "ws:";
    return `wss://api.minipix.in.net/transcoding/ws/jobs/${encodeURIComponent(
      jobId
    )}?token=${encodeURIComponent(tokenSecret)}`;
  } catch {
    return `wss://api.minipix.in.net/transcoding/ws/jobs/${encodeURIComponent(
      jobId
    )}?token=${encodeURIComponent(tokenSecret)}`;
  }
}

const TranscodingContext = createContext(null);

export function useTranscoding() {
  const ctx = useContext(TranscodingContext);
  if (!ctx) {
    throw new Error("useTranscoding must be used within TranscodingProvider");
  }
  return ctx;
}

export function TranscodingProvider({ children }) {
  const { transcodeUrl } = useContext(Context);
  const [job, setJob] = useState(null);

  const wsRef = useRef(null);
  const jobIdRef = useRef(null);
  const jobCompleteHandlerRef = useRef(null);
  const reconnectBlockedRef = useRef(false);
  const jobSnapshotRef = useRef(null);
  jobSnapshotRef.current = job;

  const registerJobCompleteHandler = useCallback((fn) => {
    jobCompleteHandlerRef.current = fn;
  }, []);

  useEffect(() => {
    jobIdRef.current = job?.jobId ?? null;
  }, [job?.jobId]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(ACTIVE_TRANSCODE_JOB_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (parsed && parsed.jobId) {
        setJob(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!job?.jobId) {
      try {
        localStorage.removeItem(ACTIVE_TRANSCODE_JOB_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    const u = String(job.status || "").toUpperCase();
    if (u === "COMPLETED" || u === "FAILED" || u === "KILLED") {
      try {
        localStorage.removeItem(ACTIVE_TRANSCODE_JOB_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      localStorage.setItem(
        ACTIVE_TRANSCODE_JOB_KEY,
        JSON.stringify({
          jobId: job.jobId,
          contentId: job.contentId,
          status: job.status,
          progress: job.progress,
          step: job.step,
          ...(job.contentTitle ? { contentTitle: job.contentTitle } : {}),
        })
      );
    } catch {
      /* ignore */
    }
  }, [job]);

  const closeWebSocket = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.onclose = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
      } catch {
        /* ignore */
      }
      wsRef.current = null;
    }
  }, []);

  const clearJob = useCallback(() => {
    reconnectBlockedRef.current = true;
    closeWebSocket();
    setJob(null);
    try {
      localStorage.removeItem(ACTIVE_TRANSCODE_JOB_KEY);
    } catch {
      /* ignore */
    }
  }, [closeWebSocket]);

  const finalizeClientKill = useCallback((statusStr) => {
    reconnectBlockedRef.current = true;
    closeWebSocket();
    setJob((prev) =>
      prev
        ? {
            ...prev,
            status: String(statusStr || "KILLED"),
            progress: 0,
          }
        : prev
    );
  }, [closeWebSocket]);

  useEffect(() => {
    const activeJobId = job?.jobId;
    if (!activeJobId || !transcodeUrl) {
      closeWebSocket();
      return undefined;
    }

    reconnectBlockedRef.current = false;

    let ws = null;
    let reconnectTimer = null;
    let cancelled = false;
    let isJobFinished = false;

    const terminalUpper = (s) => String(s || "").toUpperCase();

    const connect = () => {
      if (cancelled) return;

      clearTimeout(reconnectTimer);
      reconnectTimer = null;

      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          ws.close();
        } catch {
          /* ignore */
        }
        ws = null;
        wsRef.current = null;
      }

      const url = buildTranscodingWebSocketUrl(transcodeUrl, activeJobId, TRANSCODE_SECRET);

      let socket;
      try {
        socket = new WebSocket(url);
      } catch (e) {
        console.error(e);
        if (
          !cancelled &&
          jobIdRef.current === activeJobId &&
          !isJobFinished &&
          !reconnectBlockedRef.current
        ) {
          reconnectTimer = window.setTimeout(connect, 2000);
        }
        return;
      }

      ws = socket;
      wsRef.current = socket;

      socket.onopen = () => {
        if (cancelled || wsRef.current !== socket) return;
      };

      socket.onmessage = (event) => {
        if (wsRef.current !== socket) return;

        try {
          const payload = JSON.parse(event.data);
          const jobPayload = payload.job || {};
          const statusVal = jobPayload.status ?? payload.status;
          const stepVal = jobPayload.step ?? payload.step;
          const progressVal = jobPayload.progress ?? payload.progress;

          setJob((prev) => {
            if (!prev || prev.jobId !== activeJobId) return prev;
            const next = { ...prev };
            if (statusVal != null && statusVal !== "") {
              next.status = String(statusVal);
            }
            if (stepVal != null && stepVal !== "") {
              next.step = String(stepVal);
            }
            if (progressVal !== undefined && progressVal !== null) {
              const n =
                typeof progressVal === "number"
                  ? progressVal
                  : Number(progressVal);
              if (!Number.isNaN(n) && Number.isFinite(n)) {
                next.progress = Math.max(0, Math.min(100, n));
              }
            }
            if (jobPayload.output_dir != null && jobPayload.output_dir !== "") {
              next.outputDir = String(jobPayload.output_dir);
            }
            if (jobPayload.error != null && jobPayload.error !== "") {
              next.error = String(jobPayload.error);
            }
            return next;
          });

          const t = terminalUpper(statusVal);
          const isTerminal = t === "COMPLETED" || t === "FAILED" || t === "KILLED";

          if (isTerminal) {
            reconnectBlockedRef.current = true;
            isJobFinished = true;
            if (t === "COMPLETED") {
              const seriesName = jobSnapshotRef.current?.contentTitle;
              notification.success({
                message: "Transcoding completed",
                description: seriesName
                  ? `"${seriesName}" finished successfully.`
                  : "Vertical short transcoding finished successfully.",
                placement: "topRight",
                duration: 5,
              });
              try {
                jobCompleteHandlerRef.current?.();
              } catch {
                /* ignore */
              }
            }
            if (t === "FAILED") message.error("Transcoding failed");
            if (t === "KILLED") message.info("Transcoding was cancelled");
            if (wsRef.current === socket) {
              wsRef.current = null;
            }
            try {
              socket.onopen = null;
              socket.onmessage = null;
              socket.onerror = null;
              socket.onclose = null;
              socket.close();
            } catch {
              /* ignore */
            }
          }
        } catch (err) {
          console.error("WS parse error", err);
        }
      };

      socket.onerror = () => {};

      socket.onclose = () => {
        const wasActive = wsRef.current === socket;
        if (wasActive) {
          wsRef.current = null;
        }
        if (cancelled || jobIdRef.current !== activeJobId || isJobFinished) return;
        if (!wasActive) return;
        if (reconnectBlockedRef.current) return;

        reconnectTimer = window.setTimeout(() => {
          if (cancelled || jobIdRef.current !== activeJobId || isJobFinished) return;
          if (reconnectBlockedRef.current) return;
          if (wsRef.current != null) return;
          connect();
        }, 2000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      isJobFinished = true;
      if (ws) {
        try {
          ws.onopen = null;
          ws.onmessage = null;
          ws.onerror = null;
          ws.onclose = null;
          ws.close();
        } catch {
          /* ignore */
        }
      }
      wsRef.current = null;
    };
  }, [job?.jobId, transcodeUrl, closeWebSocket]);

  const value = {
    job,
    setJob,
    clearJob,
    finalizeClientKill,
    registerJobCompleteHandler,
  };

  return (
    <TranscodingContext.Provider value={value}>{children}</TranscodingContext.Provider>
  );
}
